"use client";

import posthog from "posthog-js";

import { SOFT_LIMIT_MODEL_TRAINING_DATA_SIZE_MB } from "@/constants/constants";
import { getAiffDuration } from "@/utils/audio/aiff/getAiffDuration";
import { isAiffFile } from "@/utils/audio/aiff/isAiffFile";
import { getAudioLengthFromMetadata } from "@/utils/getAudioLength/getAudioLengthFromMetadata";

// Telemetry must never break length measurement, hence the inner guard
const captureAudioLengthException = (
  error: unknown,
  audioFile: File,
  extraProperties: Record<string, unknown> | null
) => {
  try {
    posthog.captureException(error, {
      fileName: audioFile.name,
      fileSize: audioFile.size,
      fileType: audioFile.type,
      ...(extraProperties ?? {}),
    });
  } catch (e) {
    console.warn("Failed to track audio length error:", e);
  }
};

const AUDIO_METADATA_TIMEOUT_MS = 15_000;

const getDurationFromAudioElement = (audioFile: File): Promise<number> => {
  return new Promise<number>((resolve, reject) => {
    const audio = document.createElement("audio");
    const objectURL = URL.createObjectURL(audioFile);

    // Cleanup function to prevent memory leaks
    const cleanup = () => {
      URL.revokeObjectURL(objectURL);
      audio.src = "";
      audio.load(); // Reset audio element
      audio.remove(); // Remove from memory
    };

    // Timeout to prevent infinite hangs and memory leaks. A file that stalls without ever firing loadedmetadata or error is a real decode failure; captured here like the other failure paths, since the callers only show the message.
    const timeoutId = setTimeout(() => {
      const timeoutError = new Error(
        `Audio metadata loading timeout after ${AUDIO_METADATA_TIMEOUT_MS / 1000} seconds`
      );
      captureAudioLengthException(timeoutError, audioFile, null);
      cleanup();
      reject(timeoutError);
    }, AUDIO_METADATA_TIMEOUT_MS);

    audio.addEventListener(
      "loadedmetadata",
      () => {
        clearTimeout(timeoutId);
        const duration = Math.floor(audio.duration);
        cleanup();
        resolve(duration);
      },
      { once: true }
    );

    audio.addEventListener(
      "error",
      () => {
        clearTimeout(timeoutId);

        const errorCode = audio.error?.code;
        const errorMessage = audio.error?.message;

        const audioError = new Error(
          `Failed to load audio metadata: code=${errorCode}, message=${errorMessage}`
        );

        captureAudioLengthException(audioError, audioFile, {
          errorCode,
          errorMessage,
        });

        cleanup();
        reject(audioError);
      },
      { once: true }
    );

    // Explicitly start loading
    audio.src = objectURL;
    audio.load();
  });
};

export const getAudioLengthClient = async (
  audioFile: File
): Promise<number> => {
  // Header/frame parsing is the primary measurement: it is decoder-independent, streams the file instead of decoding it to PCM, and matches the server-side measurement. The decode-based paths below remain as fallbacks for containers it cannot read.
  try {
    const metadataDuration = await getAudioLengthFromMetadata(audioFile);
    if (metadataDuration !== null) {
      return metadataDuration;
    }
  } catch (error) {
    captureAudioLengthException(error, audioFile, null);
  }

  const audioFileSizeMb = audioFile.size / 1024 / 1024;

  // For AIFF files, use custom parser
  if (isAiffFile(audioFile)) {
    try {
      const duration = await getAiffDuration(audioFile);
      return Math.floor(duration);
    } catch (error) {
      console.warn(
        "AIFF parsing failed, falling back to audio element:",
        error
      );
      return getDurationFromAudioElement(audioFile);
    }
  }

  if (audioFileSizeMb > SOFT_LIMIT_MODEL_TRAINING_DATA_SIZE_MB) {
    // Use <audio> element for large files
    return getDurationFromAudioElement(audioFile);
  }

  // For smaller files, try AudioContext first, fallback to audio element
  let audioContext: AudioContext | null = null;
  try {
    const audioBuffer = await audioFile.arrayBuffer();
    audioContext = new AudioContext();
    const decodedAudioData = await audioContext.decodeAudioData(audioBuffer);
    const duration = Math.floor(decodedAudioData.duration);

    // Close AudioContext before returning
    await audioContext.close();
    return duration;
  } catch (error) {
    // Ensure AudioContext is always closed, even on error
    if (audioContext !== null && audioContext.state !== "closed") {
      try {
        await audioContext.close();
      } catch (closeError) {
        console.warn("Failed to close AudioContext on error:", closeError);
      }
    }

    captureAudioLengthException(error, audioFile, null);

    return getDurationFromAudioElement(audioFile);
  }
};
